import { useState } from "react";
import api from "../lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Calendar, User, FileSpreadsheet, PenLine, ChevronDown, ChevronRight } from "lucide-react";
import KeyInsights from "./KeyInsights";

/**
 * Renders the read-only report (indicator breakdown + click-to-verify).
 * `auditFetcher(indicatorId, period)` returns the audit rows.
 */
export default function ReportContent({ data, report, auditFetcher, showInsights = true }) {
  const [view, setView] = useState("compact");
  const [showDataset, setShowDataset] = useState(false);
  return (
    <>
      {data?.program?.organization && (data.program.organization.logo || data.program.organization.name) && (
        <div className="px-6 pt-6 pb-3 border-b border-slate-100 flex items-center gap-4" data-testid="report-org-branding">
          {data.program.organization.logo && (
            <img
              src={data.program.organization.logo}
              alt={data.program.organization.name || "Organization"}
              className="h-10 w-auto max-w-[140px] object-contain bg-white rounded-sm ring-1 ring-slate-200 p-1"
            />
          )}
          <div>
            {data.program.organization.name && (
              <div className="text-sm font-semibold text-slate-900" data-testid="report-org-name">
                {data.program.organization.name}
              </div>
            )}
            {data.program.organization.industry && (
              <div className="label-overline mt-0.5">{data.program.organization.industry}</div>
            )}
          </div>
          <div className="ml-auto h-1 flex-1 max-w-[140px] bg-emerald-500/20 relative">
            <div className="absolute inset-y-0 left-0 bg-emerald-500 w-1/3"></div>
          </div>
        </div>
      )}
      <div className="px-6 pt-8 pb-2">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold" data-testid="report-title">{report.title}</h1>
        {report.period && <p className="label-overline mt-2">Period · {report.period}</p>}
      </div>

      {showInsights && <KeyInsights />}

      {report.blocks?.length > 0 && (
        <div className="px-6 pb-2 space-y-6 pt-6 border-t border-slate-100">
          {report.blocks.map((b) => (
            <div key={b.key} data-testid={`public-block-${b.key}`}>
              <p className="label-overline">{b.title}</p>
              <p className="mt-2 text-base leading-relaxed text-slate-800 whitespace-pre-wrap">{b.content}</p>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-100 px-6 py-4 mt-4" data-testid="full-dataset-disclosure">
        <button
          type="button"
          onClick={() => setShowDataset(!showDataset)}
          className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 font-medium"
          data-testid="toggle-full-dataset"
          aria-expanded={showDataset}
        >
          {showDataset
            ? <ChevronDown className="w-4 h-4" />
            : <ChevronRight className="w-4 h-4" />}
          {showDataset ? "Hide full dataset" : "Show full dataset"}
          <span className="text-slate-400 font-normal text-xs">
            · {data.breakdown.length} indicator{data.breakdown.length === 1 ? "" : "s"} · click-to-verify audit trail
          </span>
        </button>
      </div>

      {showDataset && (
      <div className="border-t border-slate-100 p-6 mt-0">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="label-overline">Indicator results</p>
            <h3 className="font-heading text-2xl mt-1">Quantitative dataset</h3>
            <p className="text-sm text-slate-500 mt-1">Click any number to verify its source.</p>
          </div>
          <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden text-xs" data-testid="indicator-view-toggle">
            <button
              type="button"
              onClick={() => setView("compact")}
              className={`px-3 py-1.5 ${view === "compact" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="view-toggle-compact"
            >
              Compact
            </button>
            <button
              type="button"
              onClick={() => setView("detailed")}
              className={`px-3 py-1.5 ${view === "detailed" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="view-toggle-detailed"
            >
              Detailed
            </button>
          </div>
        </div>

        {view === "compact" ? (
          <CompactResults data={data} auditFetcher={auditFetcher} />
        ) : (
          <div className="mt-6 space-y-6">
            {data.breakdown.map((item) => (
              <IndicatorBlock key={item.indicator.id} item={item} auditFetcher={auditFetcher} />
            ))}
          </div>
        )}
      </div>
      )}
    </>
  );
}

function summarizePeriods(periods) {
  // periods: ["Q1-2025", "Q2-2025", "Q3-2025", ...] — collapse runs to a friendly label
  if (!periods || periods.length === 0) return "—";
  const sorted = [...periods].sort();
  if (sorted.length === 1) return sorted[0];
  // If they all share a year, show year(s) joined
  const years = Array.from(new Set(sorted.map((p) => (p.match(/\d{4}/) || [])[0]).filter(Boolean)));
  if (sorted.length <= 4) return sorted.join(", ");
  if (years.length === 1) return `${sorted[0]} → ${sorted[sorted.length - 1]}  ·  ${sorted.length} periods`;
  return `${years[0]} – ${years[years.length - 1]}  ·  ${sorted.length} periods`;
}

function extractYear(period) {
  // Pulls a 4-digit year from any period string. Returns null if none.
  const m = String(period || "").match(/\d{4}/);
  return m ? m[0] : null;
}

function groupPeriodsByYear(periods) {
  // periods: [{period, value, count, ...}] for one indicator. Returns
  //   [{ year, value, count, sourcePeriods: [{period, value, count}] }, ...]
  // sorted by year ascending. Periods with no detectable year fall under
  // a single "Other" bucket so nothing is silently dropped.
  const buckets = new Map();
  for (const p of periods || []) {
    const year = extractYear(p.period) || "Other";
    if (!buckets.has(year)) {
      buckets.set(year, { year, value: 0, count: 0, sourcePeriods: [] });
    }
    const b = buckets.get(year);
    b.value += Number(p.value || 0);
    b.count += Number(p.count || 0);
    b.sourcePeriods.push(p);
  }
  // Sort: actual years ascending, "Other" last
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.year === "Other") return 1;
    if (b.year === "Other") return -1;
    return a.year.localeCompare(b.year);
  });
}

function CompactResults({ data, auditFetcher }) {
  // ONE row per (indicator, year) — periods inside a year are summed for
  // numeric indicators or counted for qualitative. A grand-total row is
  // appended when an indicator has data across multiple years.
  const rows = [];
  for (const item of data.breakdown) {
    const ind = item.indicator;
    const periods = item.periods || [];
    if (periods.length === 0) {
      rows.push({
        kind: "empty",
        indicator: ind,
        periodLabel: "—",
        valueDisplay: "No data",
      });
      continue;
    }
    const years = groupPeriodsByYear(periods);
    for (const yb of years) {
      const sourcePeriods = yb.sourcePeriods.map((p) => p.period);
      rows.push({
        kind: "year",
        indicator: ind,
        year: yb.year,
        periodLabel: yb.year + (sourcePeriods.length > 1 ? `  ·  ${sourcePeriods.length} periods` : ""),
        sourcePeriods,
        sourceCount: yb.count,
        valueDisplay:
          ind.data_type === "numeric"
            ? `${yb.value.toLocaleString()} ${ind.unit || ""}`.trim()
            : `${yb.count} entr${yb.count === 1 ? "y" : "ies"}`,
        clickable: ind.data_type === "numeric",
      });
    }
    if (ind.data_type === "numeric" && item.total !== null && years.length > 1) {
      rows.push({
        kind: "total",
        indicator: ind,
        periodLabel: summarizePeriods(years.map((y) => y.year)),
        valueDisplay: `${item.total.toLocaleString()} ${ind.unit || ""}`.trim(),
        sourceCount: years.reduce((s, y) => s + (y.count || 0), 0),
      });
    }
  }

  return (
    <div className="mt-6 border border-slate-200 rounded-sm overflow-hidden" data-testid="indicator-compact-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-900 text-white text-left">
            <th className="px-4 py-3 font-medium w-28">Code</th>
            <th className="px-4 py-3 font-medium">Name</th>
            <th className="px-4 py-3 font-medium w-44">Period</th>
            <th className="px-4 py-3 font-medium text-right w-44">Value</th>
            <th className="px-4 py-3 font-medium text-right w-20">Rows</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const isTotal = r.kind === "total";
            const isEmpty = r.kind === "empty";
            return (
              <tr
                key={`${r.indicator.id}-${i}`}
                className={`border-t border-slate-100 ${isTotal ? "bg-slate-50 font-medium" : ""}`}
                data-testid={`compact-row-${r.indicator.code}-${r.year || (isTotal ? "total" : "empty")}`}
              >
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.indicator.code}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-800">{r.indicator.name}</div>
                  {r.indicator.unit && (
                    <div className="text-[11px] text-slate-400">{r.indicator.unit} · {r.indicator.data_type}</div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  {isTotal ? (
                    <span className="label-overline text-slate-500">Total · {r.periodLabel}</span>
                  ) : (
                    <span title={(r.sourcePeriods || []).join(", ")}>{r.periodLabel}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular">
                  {isEmpty ? (
                    <span className="text-slate-400 text-xs">{r.valueDisplay}</span>
                  ) : r.clickable && r.sourcePeriods?.length === 1 ? (
                    // Single period in this year → still click-to-verify the source row.
                    <ClickToVerify
                      auditFetcher={auditFetcher}
                      indicatorId={r.indicator.id}
                      period={r.sourcePeriods[0]}
                      display={r.valueDisplay}
                    />
                  ) : (
                    <span className="font-mono">{r.valueDisplay}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-slate-500 text-xs">{isEmpty ? "" : r.sourceCount ?? ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function IndicatorBlock({ item, auditFetcher }) {
  const ind = item.indicator;
  const byAxis = item.by_axis || [];
  const [showAxes, setShowAxes] = useState(false);
  return (
    <div className="border border-slate-200 rounded-sm" data-testid={`indicator-block-${ind.code}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="font-mono text-xs text-slate-500 mr-2">{ind.code}</span>
          <span className="font-heading text-base font-medium">{ind.name}</span>
          <span className="ml-2 text-xs text-slate-500">{ind.unit && `(${ind.unit})`}</span>
        </div>
        <div className="flex gap-1 flex-wrap">
          {ind.tags.map((t, i) => (
            <span key={i} className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{t.key}: {t.value}</span>
          ))}
        </div>
      </div>
      {item.periods.length === 0 ? (
        <div className="px-4 py-6 text-center text-slate-500 text-sm">No data yet for this indicator.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left border-b border-slate-200">
              <th className="px-4 py-2 font-medium w-40">Period</th>
              <th className="px-4 py-2 font-medium">{ind.data_type === "numeric" ? "Value" : "Entries"}</th>
              <th className="px-4 py-2 font-medium w-32 text-right">Source rows</th>
            </tr>
          </thead>
          <tbody>
            {item.periods.map((p) => (
              <tr key={p.period} className="border-b border-slate-100 last:border-b-0">
                <td className="px-4 py-3 font-mono text-xs">{p.period}</td>
                <td className="px-4 py-3">
                  {ind.data_type === "numeric" ? (
                    <ClickToVerify auditFetcher={auditFetcher} indicatorId={ind.id} period={p.period} display={`${p.value.toLocaleString()} ${ind.unit || ""}`} />
                  ) : (
                    <div className="space-y-1">
                      {p.texts.map((t, i) => (
                        <ClickToVerify key={i} auditFetcher={auditFetcher} indicatorId={ind.id} period={p.period} display={t} block />
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-slate-500 text-xs">{p.count}</td>
              </tr>
            ))}
            {ind.data_type === "numeric" && item.total !== null && (
              <tr className="bg-slate-900 text-white">
                <td className="px-4 py-3 label-overline text-slate-300">Total</td>
                <td className="px-4 py-3 font-mono">{item.total.toLocaleString()} {ind.unit || ""}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {byAxis.length > 0 && (
        <DisaggregationSection axes={byAxis} unit={ind.unit} total={item.total} indCode={ind.code} expanded={showAxes} onToggle={() => setShowAxes(!showAxes)} />
      )}
    </div>
  );
}

function DisaggregationSection({ axes, unit, total, indCode, expanded, onToggle }) {
  const fmt = (v) => Number(v || 0).toLocaleString();
  return (
    <div className="border-t border-slate-100" data-testid={`disagg-${indCode}`}>
      <div className="px-4 py-3 flex items-start gap-3 flex-wrap">
        <span className="label-overline text-slate-500 shrink-0 mt-0.5">Disaggregation</span>
        <div className="flex flex-wrap gap-x-6 gap-y-1 flex-1 min-w-0">
          {axes.map((a) => (
            <div key={a.axis_key} className="text-xs text-slate-700">
              <span className="font-mono uppercase tracking-wider text-slate-500 mr-1">{a.axis_key}:</span>
              {a.slices.slice(0, 4).map((s, i) => (
                <span key={s.label}>
                  {i > 0 && " · "}
                  <span className="font-medium">{s.label}</span> {fmt(s.value)}{unit ? ` ${unit}` : ""}
                </span>
              ))}
              {a.slices.length > 4 && <span className="text-slate-400"> · +{a.slices.length - 4} more</span>}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="text-[11px] tracking-wider uppercase text-slate-500 hover:text-slate-900"
          data-testid={`disagg-toggle-${indCode}`}
        >
          {expanded ? "Hide breakdown" : "View breakdown"}
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {axes.map((a) => (
            <div key={a.axis_key} className="border border-slate-200 rounded-sm overflow-hidden" data-testid={`disagg-table-${indCode}-${a.axis_key}`}>
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 label-overline">By {a.axis_key}</div>
              <table className="w-full text-xs">
                <tbody>
                  {a.slices.map((s) => {
                    const pct = total ? Math.round((s.value / total) * 100) : 0;
                    return (
                      <tr key={s.label} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-3 py-2">{s.label}</td>
                        <td className="px-3 py-2 font-mono text-right">{fmt(s.value)}{unit ? ` ${unit}` : ""}</td>
                        <td className="px-3 py-2 text-right text-slate-500 w-12">{pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClickToVerify({ auditFetcher, indicatorId, period, display, block }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (rows) return;
    setLoading(true);
    const data = await auditFetcher(indicatorId, period);
    setRows(data);
    setLoading(false);
  };
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <span
          className={`click-to-verify font-mono ${block ? "block py-0.5" : "inline-block"}`}
          data-testid={`value-click-${indicatorId}-${period}`}
          data-indicator-id={indicatorId}
          data-period={period}
        >
          {display}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="label-overline">Audit trail</p>
          <p className="text-sm font-medium mt-1">{display}</p>
          <p className="text-xs text-slate-500 mt-1">Period: <span className="font-mono">{period}</span></p>
        </div>
        <div className="p-4 max-h-72 overflow-y-auto">
          {loading && <div className="text-xs text-slate-500">Loading audit trail…</div>}
          {rows && rows.length === 0 && <div className="text-xs text-slate-500">No source rows.</div>}
          {rows && rows.map((r) => (
            <div key={r.id} className="border border-slate-200 rounded-sm p-3 mb-2 last:mb-0" data-testid={`audit-row-${r.id}`}>
              <div className="text-[10px] tracking-wider uppercase text-slate-500">SourceID</div>
              <div className="font-mono text-xs break-all">{r.id}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <div className="flex items-center gap-1 text-slate-600"><FileSpreadsheet className="w-3 h-3"/> {r.filename}</div>
                <div className="flex items-center gap-1 text-slate-600"><User className="w-3 h-3"/> {r.uploaded_by_name}</div>
                <div className="flex items-center gap-1 text-slate-600 col-span-2"><Calendar className="w-3 h-3"/> {new Date(r.created_at).toLocaleString()}</div>
              </div>
              {r.manual_fix && (
                <div className="mt-2 pt-2 border-t border-amber-200" data-testid={`audit-manual-fix-${r.id}`}>
                  <div className="text-[10px] tracking-wider uppercase text-amber-700 mb-1 flex items-center gap-1">
                    <PenLine className="w-3 h-3" /> Manually corrected · transparent
                  </div>
                  <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 p-2 rounded-sm leading-relaxed">
                    Original upload value{" "}
                    <span className="font-mono line-through text-amber-700">{String(r.original_row?.value ?? "—")}</span>
                    {" "}was corrected to{" "}
                    <span className="font-mono font-semibold">{String(r.value_raw)}</span>
                    {" "}by <span className="font-medium">{r.manual_fix.by_name || r.manual_fix.by_email || "an editor"}</span>
                    {" "}on {new Date(r.manual_fix.ts).toLocaleDateString()}.
                  </div>
                </div>
              )}
              <div className="mt-2 pt-2 border-t border-slate-100">
                <div className="text-[10px] tracking-wider uppercase text-slate-500 mb-1">Original row</div>
                <pre className="text-[11px] bg-slate-50 p-2 rounded border border-slate-200 overflow-x-auto">{JSON.stringify(r.original_row, null, 2)}</pre>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { ClickToVerify };
